import React, { useCallback, useEffect, useState } from "react";
import {
    Drawer, Stack, Text, Button, Card, IconPrint, Badge, Textarea, Inline, TabsProvider, Tabs, Tab, TabPanels, TabPanel, Columns, Column, Divider, TextField,
    Box,
    Actions,
    IconArrow,
} from "braid-design-system";
import { unifiedTransactionService } from "../services/unifiedTransactionService";
import { useToast } from "../hooks/useToast";
import { useWebSocket } from "../contexts/WebSocketProvider";
import { WEBSOCKET_MESSAGE_TYPES } from "../constants/webSocketTypes";
import { WebSocketResponse } from "../schemas/webSocketResponseSchema";
import { formatCurrency } from "../utils/currencyUtils";
import { formatPrintDate } from "../utils/dateUtils";
import TransactionPrintDrawer from "./Transactions/TransactionPrintDrawer";

interface TransactionItem {
    uuid: string;
    msg_id?: string;
    transaction_type: string;
    total_amount: number;
    submitted_by_name: string;
    submitted_at: string;
    ref_account_number?: string;
    ref_account_name?: string;
    status?: string;
    payload?: any;
    approval_level?: number;
    submitter_level?: number;
    max_approval_level?: number;
    total_approval_steps?: number;
    // For approval-stream items
    id?: string;
    submitted_by?: string;
    is_approval_stream?: boolean;
    rejected_by_name?: string;
    rejected_reason?: string;
}

interface TransactionApprovalDrawerProps {
    transactionType: string;
    open: boolean;
    onClose: () => void;
    onRefreshCounts: () => void;
}

const ALL_TYPES = ['receipt', 'payment', 'withdrawal', 'journal'];
const TYPE_LABELS: Record<string, string> = {
    receipt: "Receipt",
    payment: "Payment",
    withdrawal: "Withdrawal",
    journal: "Journal",
};

const TransactionApprovalDrawer: React.FC<TransactionApprovalDrawerProps> = ({
    transactionType: initialType,
    open,
    onClose,
    onRefreshCounts,
}) => {
    const toast = useToast();
    const [allPending, setAllPending] = useState<TransactionItem[]>([]);
    const [allApproved, setAllApproved] = useState<TransactionItem[]>([]);
    const [allRejected, setAllRejected] = useState<TransactionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [rejectingId, setRejectingId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [executingId, setExecutingId] = useState<string | null>(null);
    const [filterType, setFilterType] = useState<string>(initialType === 'all' ? 'all' : initialType);

    // Recent tab pagination
    const [recentItems, setRecentItems] = useState<TransactionItem[]>([]);
    const [recentTotal, setRecentTotal] = useState(0);
    const [recentOffset, setRecentOffset] = useState(0);
    const [recentLoading, setRecentLoading] = useState(false);

    const RECENT_PAGE_SIZE = 10;

    // Credential dialog state
    const [credentialDialogOpen, setCredentialDialogOpen] = useState(false);
    const [credentialLoading, setCredentialLoading] = useState(false);
    const [approvingTx, setApprovingTx] = useState<TransactionItem | null>(null);
    const [credentialUsername, setCredentialUsername] = useState('');
    const [credentialPassword, setCredentialPassword] = useState('');
    const [actionType, setActionType] = useState<'APPROVE' | 'REJECT'>('APPROVE');
    const [wsCompletedInfo, setWsCompletedInfo] = useState<Record<string, { gl_transaction_header_id: number; transaction_number: string }>>({});
    const [queueIdByUuid, setQueueIdByUuid] = useState<Record<string, string>>({});
    const [printPayloads, setPrintPayloads] = useState<Record<string, { transaction_number: string; entered_date?: string; updated_date?: string; payload: any }>>({});
    const [completedHeaderIds, setCompletedHeaderIds] = useState<Record<string, number>>({});
    const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
    const [printTxId, setPrintTxId] = useState<number | null>(null);

    useEffect(() => {
        setCredentialUsername('');
        setCredentialPassword('');
    }, [open, credentialDialogOpen]);

    useEffect(() => {
        if (!open) {
            setWsCompletedInfo({});
            setQueueIdByUuid({});
            setPrintPayloads({});
            setCompletedHeaderIds({});
            setRecentItems([]);
            setRecentOffset(0);
            setRecentTotal(0);
        }
    }, [open]);

    useEffect(() => {
        setFilterType(initialType === 'all' ? 'all' : initialType);
    }, [initialType]);

    const fetchAll = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const typesToFetch = initialType === 'all' ? ALL_TYPES : [initialType];
            const allP: TransactionItem[] = [];
            const allA: TransactionItem[] = [];
            const allR: TransactionItem[] = [];

            // 1. Fetch own cached transactions
            await Promise.all(typesToFetch.map(async (t) => {
                const txType = t.toUpperCase();
                const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
                    unifiedTransactionService.getCachedTransactions(txType, "pending").catch(() => ({ transactions: [] })),
                    unifiedTransactionService.getCachedTransactions(txType, "approve").catch(() => ({ transactions: [] })),
                    unifiedTransactionService.getCachedTransactions(txType, "reject").catch(() => ({ transactions: [] })),
                ]);

                const mapTx = (item: any, status: string): TransactionItem => ({
                    uuid: item.uuid || "",
                    msg_id: item.msg_id || "",
                    transaction_type: (item.transaction_type || t).toLowerCase(),
                    total_amount: parseFloat(item.totalAmount || 0),
                    submitted_by_name: item.submitted_by_name || "",
                    submitted_at: item.submitted_at || item.payload?.submitted_at || "",
                    ref_account_number: item.payload?.display_transaction_number || item.ref_account_number || item.payload?.ref_account_number || item.payload?.account_number || "",
                    ref_account_name: [item.payload?.customer_number, item.payload?.customer_name].filter(Boolean).join(' - ') || item.ref_account_name || item.payload?.ref_account_name || item.payload?.account_name || "",
                    status: item.status || status,
                    payload: item.payload || {},
                    approval_level: item.approval_level,
                    rejected_by_name: item.payload?.rejected_by_name || item.payload?.rejected_by || item.rejected_by_name || item.rejected_by || "",
                    rejected_reason: item.payload?.rejected_reason || item.payload?.rejection_reason || item.rejected_reason || item.rejection_reason || "",
                });

                allP.push(...(pendingRes?.transactions || []).map((item: any) => mapTx(item, "PENDING")));
                allA.push(...(approvedRes?.transactions || []).map((item: any) => mapTx(item, "APPROVED")));
                allR.push(...(rejectedRes?.transactions || []).map((item: any) => mapTx(item, "REJECTED")));
            }));

            // Also fetch rejected from DB (filtered by working date) — authoritative source
            try {
                const rejectedDbRes = await unifiedTransactionService.getRejectedTransactions(50);
                const dbUuids = new Set((rejectedDbRes.transactions || []).map((i: any) => i.uuid));
                // Remove cache entries not in DB (stale data)
                for (let i = allR.length - 1; i >= 0; i--) {
                    if (!dbUuids.has(allR[i].uuid)) {
                        allR.splice(i, 1);
                    }
                }
                // Add DB entries not already in cache
                for (const item of rejectedDbRes.transactions || []) {
                    if (allR.some(t => t.uuid === item.uuid)) continue;
                    allR.push({
                        uuid: item.uuid || "",
                        msg_id: item.msg_id || "",
                        transaction_type: (item.transaction_type || '').toLowerCase(),
                        total_amount: parseFloat(item.total_amount || 0),
                        submitted_by_name: item.payload?.submitted_by_name || "",
                        submitted_at: item.submitted_at || "",
                        ref_account_number: item.payload?.display_transaction_number || "",
                        ref_account_name: [item.payload?.customer_number, item.payload?.customer_name].filter(Boolean).join(' - ') || "",
                        status: "REJECTED",
                        payload: item.payload || {},
                        rejected_by_name: item.payload?.rejected_by_name || item.payload?.rejected_by || item.rejected_by_name || item.rejected_by || "",
                        rejected_reason: item.payload?.rejected_reason || item.payload?.rejection_reason || item.rejected_reason || item.rejection_reason || "",
                    });
                }
            } catch (e) { /* non-critical */ }

            // 3. Fetch approval-stream transactions (transactions needing THIS user's approval)
            try {
                const levelsResponse = await unifiedTransactionService.getUserApprovalLevels();
                const levels = levelsResponse.approval_levels || [1];
                const queuePromises = levels.map((level: number) =>
                    unifiedTransactionService.getPendingApprovals(level, true)
                        .then((res: any) => ({ level, transactions: res.transactions || [] }))
                );
                const queueResults = await Promise.all(queuePromises);

                // Build a set of UUIDs already in own-cache to avoid duplicates
                const ownUuids = new Set(allP.map(t => t.uuid));

                for (const queueResult of queueResults) {
                    for (const item of queueResult.transactions) {
                        const itemUuid = item.uuid || item.id || "";

                        // If already in own-cache, enrich it with approval_level from the stream, then continue
                        if (ownUuids.has(itemUuid)) {
                            const existing = allP.find(t => t.uuid === itemUuid);
                            if (existing) {
                                existing.approval_level = queueResult.level;
                                existing.is_approval_stream = true;
                                existing.msg_id = item.msg_id || item.id || existing.msg_id || "";
                            }
                            continue;
                        }

                        const txType = (item.transaction_type || '').toLowerCase();
                        // Only include types matching the filter
                        if (initialType !== 'all' && txType !== initialType) continue;

                        const payload: any = item.payload || {};
                        allP.push({
                            uuid: itemUuid,
                            msg_id: item.msg_id || item.id || "",
                            id: item.id || "",
                            transaction_type: txType,
                            total_amount: parseFloat(item.total_amount || item.totalAmount || 0),
                            submitted_by_name: item.submitted_by_name || "",
                            submitted_at: item.submitted_at || "",
                            ref_account_number: payload.display_transaction_number || payload.account_number || "",
                            ref_account_name: [payload.customer_number, payload.customer_name].filter(Boolean).join(' - ') || item.submitted_by_name || "",
                            status: "PENDING",
                            payload: payload,
                            approval_level: parseInt(item.approval_level) || queueResult.level,
                            submitter_level: parseInt(item.submitter_level) || undefined,
                            max_approval_level: parseInt(item.max_approval_level) || undefined,
                            total_approval_steps: parseInt(item.total_approval_steps) || (item.submitter_level && item.max_approval_level ? Math.max(1, parseInt(item.max_approval_level) - parseInt(item.submitter_level)) : undefined),
                            submitted_by: item.submitted_by,
                            is_approval_stream: true,
                        });
                    }
                }
            } catch (e) {
                // Approval levels fetch is non-critical
            }

            setAllPending(allP);
            setAllApproved(allA);
            setAllRejected(allR);
        } finally {
            setLoading(false);
        }
    }, [initialType, open]);

    // Load recent transactions with pagination
    const loadRecent = useCallback(async (append: boolean = false) => {
        setRecentLoading(true);
        try {
            const res = await unifiedTransactionService.getRecentTransactions(RECENT_PAGE_SIZE, append ? recentOffset : 0);
            const items: TransactionItem[] = (res.transactions || []).map((item: any) => {
                const pl = item.payload || {};
                return {
                    uuid: item.uuid || "",
                    transaction_type: (item.transaction_type || '').toLowerCase(),
                    total_amount: parseFloat(item.total_amount || 0),
                    submitted_by_name: pl.submitted_by_name || pl.entered_by_name || "",
                    submitted_at: item.submitted_at || "",
                    status: item.status || "",
                    payload: pl,
                };
            });
            setRecentItems(prev => append ? [...prev, ...items] : items);
            setRecentTotal(res.count || 0);
            setRecentOffset(prev => append ? prev + RECENT_PAGE_SIZE : RECENT_PAGE_SIZE);
        } catch (e) {
            // failed to load — will show empty state
        } finally {
            setRecentLoading(false);
        }
    }, [recentOffset]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { if (open) { setRecentItems([]); setRecentOffset(0); loadRecent(false); } }, [open]);

    const { onMessage, offMessage } = useWebSocket();

    // Map WS unique_id prefix per transaction type
    const wsPrefixByType: Record<string, string> = {
        receipt: 'TRANSACTION:RECEIPT:',
        payment: 'TRANSACTION:PAYMENT:',
        withdrawal: 'TRANSACTION:WITHDRAWAL:',
        journal: 'TRANSACTION:JOURNAL:',
    };

    // Reverse map: unique_id prefix -> transaction type
    const typeByWsPrefix: Record<string, string> = {};
    for (const [type, prefix] of Object.entries(wsPrefixByType)) {
        typeByWsPrefix[prefix] = type;
    }

    useEffect(() => {
        if (!onMessage || !offMessage) return;
        const handleMessage = (message: WebSocketResponse<any>) => {
            if (message.type === WEBSOCKET_MESSAGE_TYPES.TRANSACTION_APPROVAL_PENDING) {
                fetchAll();
                loadRecent(false);
                return;
            }

            // Handle completion events
            const completionTypes = new Set<string>([
                WEBSOCKET_MESSAGE_TYPES.TRANSACTION_RECEIPT_COMPLETED,
                WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAYMENT_COMPLETED,
                WEBSOCKET_MESSAGE_TYPES.TRANSACTION_WITHDRAWAL_COMPLETED,
                WEBSOCKET_MESSAGE_TYPES.TRANSACTION_JOURNAL_COMPLETED,
            ]);
            if (!completionTypes.has(message.type)) return;

            // Always refresh on completion
            fetchAll();
            loadRecent(false);

            // Extract queue_id from unique_id for print button lookup
            let matchedUuid: string | null = null;
            for (const [prefix] of Object.entries(typeByWsPrefix)) {
                if (message.unique_id.startsWith(prefix)) {
                    const queueId = message.unique_id.slice(prefix.length);
                    for (const [uuid, qid] of Object.entries(queueIdByUuid)) {
                        if (qid === queueId) {
                            matchedUuid = uuid;
                            break;
                        }
                    }
                    break;
                }
            }

            if (!matchedUuid) return;
            const wsData = message.data || {};
            const headerId = wsData.gl_transaction_header_id;
            const txnNumber = wsData.transaction_number || '';

            if (headerId) {
                setWsCompletedInfo(prev => ({
                    ...prev,
                    [matchedUuid!]: { gl_transaction_header_id: headerId, transaction_number: txnNumber }
                }));
                setCompletedHeaderIds(prev => ({ ...prev, [matchedUuid!]: headerId }));
            }
        };
        onMessage(handleMessage);
        return () => offMessage(handleMessage);
    }, [onMessage, offMessage, fetchAll, loadRecent, queueIdByUuid]);

    const filtered = (items: TransactionItem[]) =>
        filterType === 'all' ? items : items.filter(t => t.transaction_type === filterType);

    const filterPending = filtered(allPending);
    const filterApproved = filtered(allApproved);
    const filterRejected = filtered(allRejected);
    const filterRecent = filtered(recentItems);

    const handleExecute = async (tx: TransactionItem) => {
        setExecutingId(tx.uuid);
        try {
            const result = await unifiedTransactionService.executeTransaction(tx.uuid, tx.transaction_type.toUpperCase());
            const queueId = result?.data?.result?.data?.queue_id || '';

            if (queueId) {
                setQueueIdByUuid(prev => ({ ...prev, [tx.uuid]: queueId }));
            }

            onRefreshCounts();
            toast.showSuccess(
                'Transaction Submitted',
                'Transaction has been queued for processing. You will be notified upon completion.'
            );
            fetchAll();
            loadRecent(false);
            window.dispatchEvent(new CustomEvent('transaction-completed', { detail: { uuid: tx.uuid, action: 'execute', type: tx.transaction_type } }));
        } catch (err: any) {
            toast.showError(err.message || "Execution failed");
        } finally { setExecutingId(null); }
    };

    const handlePrintCompletedTx = async (tx: TransactionItem) => {
        let headerId = completedHeaderIds[tx.uuid] || tx.payload?.gl_transaction_header_id;
        if (!headerId) {
            try {
                const statusData = await unifiedTransactionService.getTransactionStatus(tx.uuid);
                headerId = statusData?.gl_transaction_header_id;
                if (headerId) {
                    setCompletedHeaderIds(prev => ({ ...prev, [tx.uuid]: headerId }));
                }
            } catch (err: any) {
                toast.showError('Print Error', 'Failed to retrieve print details for the completed transaction.');
                return;
            }
        }

        if (headerId) {
            setPrintTxId(headerId);
        } else {
            toast.showError('Print Error', 'Transaction print document is not ready yet. Please try again in a moment.');
        }
    };

    const handleApproveClick = (tx: TransactionItem) => {
        setApprovingTx(tx);
        setActionType('APPROVE');
        setCredentialUsername('');
        setCredentialPassword('');
        setCredentialDialogOpen(true);
    };

    const handleCredentialSubmit = async () => {
        if (!approvingTx || !credentialUsername.trim() || !credentialPassword.trim()) return;
        setCredentialLoading(true);
        try {
            if (actionType === 'APPROVE') {
                const result = await unifiedTransactionService.processApprovalAction(
                    'APPROVE',
                    approvingTx.uuid,
                    approvingTx.msg_id || approvingTx.id || '',
                    approvingTx.approval_level || 1,
                    undefined,
                    credentialUsername.trim(),
                    credentialPassword
                );

                const responseData = result?.data?.result || result?.data || result;
                const message = responseData?.message || `Transaction ${approvingTx.uuid} Approved`;

                const msg = typeof message === 'string' ? message : 'Transaction has been approved and forwarded for processing.';
                toast.showSuccess('Approval Successful', msg);
                setCredentialDialogOpen(false);
                setApprovingTx(null);
                onRefreshCounts();
                fetchAll();
                window.dispatchEvent(new CustomEvent('transaction-completed', { detail: { uuid: approvingTx.uuid, action: 'approve', type: approvingTx.transaction_type } }));
            } else {
                await unifiedTransactionService.processApprovalAction(
                    'REJECT',
                    approvingTx.uuid,
                    approvingTx.msg_id || approvingTx.id || '',
                    approvingTx.approval_level || 1,
                    rejectionReason,
                    credentialUsername.trim(),
                    credentialPassword
                );

                toast.showSuccess('Rejection Completed', `${TYPE_LABELS[approvingTx.transaction_type] || approvingTx.transaction_type} transaction has been rejected.`);
                setCredentialDialogOpen(false);
                setApprovingTx(null);
                setRejectingId(null);
                setRejectionReason("");
                onRefreshCounts();
                fetchAll();
                window.dispatchEvent(new CustomEvent('transaction-completed', { detail: { uuid: approvingTx.uuid, action: 'reject', type: approvingTx.transaction_type } }));
            }
        } catch (err: any) {
            toast.showError(actionType === 'APPROVE' ? 'Approval Failed' : 'Rejection Failed', err.message || 'An unexpected error occurred. Please try again.');
            // Do NOT close the credential dialog on error so they can correct their credentials.
            // But clear the password field.
            setCredentialPassword('');
        } finally {
            setCredentialLoading(false);
        }
    };

    const handleReject = async (tx: TransactionItem) => {
        if (!rejectionReason.trim()) return;
        setApprovingTx(tx);
        setActionType('REJECT');
        setCredentialUsername('');
        setCredentialPassword('');
        setCredentialDialogOpen(true);
    };

    const renderAccounts = (tx: TransactionItem) => {
        const accounts = tx.payload?.accounts;
        if (!accounts || !Array.isArray(accounts) || accounts.length === 0) return null;
        const isJournal = tx.transaction_type === 'journal';
        return (
            <Stack space="xsmall">
                {accounts.map((acc: any, idx: number) => (
                    <Inline key={idx} space="small" alignY="center">
                        <Text size="xsmall" tone="secondary">
                            {acc.account_no || acc.account_number || acc.ref_account_number || acc.ref_number || ''}
                            {acc.account_name || acc.name || acc.pl_account_type_name || acc.gl_coa_name ? ` - ${acc.account_name || acc.name || acc.pl_account_type_name || acc.gl_coa_name}` : ''}
                            {acc.description ? ` (${acc.description})` : ''}
                        </Text>
                        {isJournal && acc.transaction_type === 'debit' ? (
                            <Text tone="critical" size="xsmall" weight="strong">Dr {formatCurrency(acc.amount || 0)}</Text>
                        ) : isJournal && acc.transaction_type === 'credit' ? (
                            <Text tone="positive" size="xsmall" weight="strong">Cr {formatCurrency(acc.amount || 0)}</Text>
                        ) : (
                            <Text size="xsmall" weight="strong">{formatCurrency(acc.amount || 0)}</Text>
                        )}
                    </Inline>
                ))}
            </Stack>
        );
    };

    const renderCard = (tx: TransactionItem, state: "pending" | "approved" | "rejected") => (
        <Card key={tx.uuid}>
            <Stack space="small">
                <Columns space="small" alignY="center">
                    <Column>
                        <Inline space="small" alignY="center">
                            <Badge>{TYPE_LABELS[tx.transaction_type] || tx.transaction_type}</Badge>
                            {state === "pending" && tx.submitter_level != null && tx.total_approval_steps != null && tx.approval_level ? (
                                <Badge tone="promote">approval steps {tx.total_approval_steps}</Badge>
                            ) : null}
                            {tx.submitted_at ? <Text size="small" tone="secondary">{formatPrintDate(tx.submitted_at, 'yyyy-MM-dd HH:mm')}</Text> : null}
                        </Inline>
                    </Column>
                    <Column width="content">
                        <Text weight="strong" size="large">{formatCurrency(tx.total_amount)}</Text>
                    </Column>
                </Columns>
                <Divider />
                <Columns space="small">
                    <Column>
                        <Stack space="xsmall">
                            <Text size="small" weight="strong">{tx.ref_account_number || (tx.payload?.display_transaction_number || 'No Reference')}</Text>
                            {tx.payload?.customer_number || tx.payload?.customer_name ? (
                                <Text size="xsmall" tone="secondary">
                                    {[tx.payload.customer_number, tx.payload.customer_name].filter(Boolean).join(' - ')}
                                </Text>
                            ) : null}
                            {renderAccounts(tx)}
                            {state === "rejected" && (tx.rejected_by_name || tx.rejected_reason) ? (
                                <Stack space="xxsmall">
                                    {tx.rejected_by_name ? (
                                        <Text size="xsmall" tone="critical" weight="strong">Rejected By: {tx.rejected_by_name}</Text>
                                    ) : null}
                                    {tx.rejected_reason ? (
                                        <Text size="xsmall" tone="critical">Reason: {tx.rejected_reason}</Text>
                                    ) : null}
                                </Stack>
                            ) : null}
                        </Stack>
                    </Column>
                    <Column width="content">
                        {(wsCompletedInfo[tx.uuid] || tx.payload?.gl_transaction_header_id) ? (
                            <Stack space="xsmall" align="center">
                                <Badge tone="positive">{wsCompletedInfo[tx.uuid]?.transaction_number || 'Completed'}</Badge>
                                <Button size="small" variant="soft" tone="formAccent" icon={<IconPrint />} onClick={() => handlePrintCompletedTx(tx)} >Print</Button>
                            </Stack>
                        ) : state === "approved" ? (
                            <Button size="small" variant="solid" tone="brandAccent"
                                icon={<IconArrow />}
                                loading={executingId === tx.uuid}
                                onClick={() => handleExecute(tx)}>
                                Process
                            </Button>
                        ) : state === "pending" ? (
                            rejectingId === tx.uuid ? (
                                <Stack space="xsmall">
                                    <Textarea label="Reason" placeholder="Rejection reason..."
                                        value={rejectionReason}
                                        onChange={(e: any) => setRejectionReason(e.currentTarget.value)} />
                                    <Inline space="xsmall" align="right">
                                        <Button size="small" variant="ghost"
                                            onClick={() => { setRejectingId(null); setRejectionReason(""); }}>
                                            Cancel
                                        </Button>
                                        <Button size="small" tone="critical"
                                            loading={credentialLoading && approvingTx?.uuid === tx.uuid && actionType === 'REJECT'}
                                            onClick={() => handleReject(tx)}>
                                            Confirm
                                        </Button>
                                    </Inline>
                                </Stack>
                            ) : (
                                <Inline space="xsmall">
                                    <Button size="small" tone="brandAccent"
                                        loading={credentialLoading && approvingTx?.uuid === tx.uuid && actionType === 'APPROVE'}
                                        onClick={() => handleApproveClick(tx)}>
                                        Approve
                                    </Button>
                                    <Button size="small" tone="critical" variant="ghost"
                                        onClick={() => setRejectingId(tx.uuid)}>
                                        Reject
                                    </Button>
                                </Inline>
                            )
                        ) : (
                            <Badge tone="critical">Rejected</Badge>
                        )}
                    </Column>
                </Columns>
            </Stack>
        </Card>
    );

    // Fetch print payloads for recent items that have gl_transaction_header_id
    useEffect(() => {
        const headerIds = new Set<number>();
        for (const tx of recentItems) {
            const hdrId = tx.payload?.gl_transaction_header_id || wsCompletedInfo[tx.uuid]?.gl_transaction_header_id;
            if (hdrId && !printPayloads[`hdr_${hdrId}`]) {
                headerIds.add(hdrId);
            }
        }
        for (const hdrId of headerIds) {
            unifiedTransactionService.getPrintPayload(hdrId).then(data => {
                if (data) {
                    setPrintPayloads(prev => ({ ...prev, [`hdr_${hdrId}`]: data }));
                }
            });
        }
    }, [recentItems, printPayloads, wsCompletedInfo]);

    const renderRecentCard = (tx: TransactionItem) => {
        const st = tx.status || '';
        const statusTone = st === 'EXECUTED' || st === 'COMPLETED' ? 'positive'
            : st === 'REJECTED' ? 'critical'
                : st === 'APPROVED' ? 'caution'
                    : 'neutral';

        const headerId = wsCompletedInfo[tx.uuid]?.gl_transaction_header_id || tx.payload?.gl_transaction_header_id;
        const ppKey = headerId ? `hdr_${headerId}` : tx.uuid;
        const printData = printPayloads[ppKey];
        const txnNumber = printData?.transaction_number || wsCompletedInfo[tx.uuid]?.transaction_number || tx.payload?.transaction_number || '';
        const enteredDate = printData?.entered_date ? formatPrintDate(printData.entered_date, 'yyyy-MM-dd HH:mm') : '';
        const updatedDate = printData?.updated_date ? formatPrintDate(printData.updated_date, 'yyyy-MM-dd HH:mm') : '';

        return (
            <Card key={tx.uuid}>
                <Stack space="small">
                    <Columns space="small" alignY="center">
                        <Column>
                            <Inline space="small" alignY="center">
                                <Badge>{TYPE_LABELS[tx.transaction_type] || tx.transaction_type}</Badge>
                                <Badge tone={statusTone as any}>{st}</Badge>
                            </Inline>
                        </Column>
                        <Column width="content">
                            <Text weight="strong" size="large">{formatCurrency(tx.total_amount)}</Text>
                        </Column>
                    </Columns>
                    <Columns space="small">
                        <Column>
                            <Stack space="xsmall">
                                <Text size="small" weight="strong">
                                    {txnNumber || tx.payload?.display_transaction_number || tx.payload?.ref_account_number || 'No Reference'}
                                </Text>
                                <Divider />
                                <Stack space="xsmall">
                                    {enteredDate ? <Text size="xsmall" tone="secondary">Entered: {enteredDate}</Text> : null}
                                    {updatedDate ? <Text size="xsmall" tone="secondary">Updated: {updatedDate}</Text> : null}
                                </Stack>
                                {printData?.payload?.accounts?.length ? (
                                    (() => {
                                        const accKey = ppKey;
                                        const allAccounts = printData.payload.accounts;
                                        const isExpanded = expandedAccounts.has(accKey);
                                        const visibleAccounts = isExpanded ? allAccounts : allAccounts.slice(0, 5);
                                        const hasMore = allAccounts.length > 5;
                                        const hasDebitCredit = allAccounts.some((a: any) => a.debit !== undefined || a.credit !== undefined);
                                        const gridCols = hasDebitCredit ? '1fr auto auto' : '1fr auto';
                                        return (
                                            <Stack space="xsmall">
                                                <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '8px 16px', alignItems: 'center' }}>
                                                    <Text size="xsmall" weight="strong">Account</Text>
                                                    {hasDebitCredit ? (
                                                        <>
                                                            <Text size="xsmall" weight="strong" align="right">DEBIT</Text>
                                                            <Text size="xsmall" weight="strong" align="right">CREDIT</Text>
                                                        </>
                                                    ) : (
                                                        <Text size="xsmall" weight="strong" align="right">Amount</Text>
                                                    )}
                                                    <div style={{ gridColumn: '1 / -1', height: '1px', background: 'var(--divider-background, var(--color-border, #ddd))' }} />
                                                    {visibleAccounts.map((acc: any, i: number) => {
                                                        const isDebitCredit = acc.debit !== undefined || acc.credit !== undefined;
                                                        return (
                                                            <React.Fragment key={i}>
                                                                <Text size="xsmall" tone="secondary">
                                                                    {acc.account_number || acc.ref_account_number || ''}
                                                                    {acc.account_name || acc.account_name_ln1 ? ` - ${acc.account_name || acc.account_name_ln1}` : ''}
                                                                </Text>
                                                                {isDebitCredit ? (
                                                                    <>
                                                                        <Text size="xsmall" tone="critical" weight="strong" align="right">
                                                                            {formatCurrency(acc.debit || 0)}
                                                                        </Text>
                                                                        <Text size="xsmall" tone="positive" weight="strong" align="right">
                                                                            {formatCurrency(acc.credit || 0)}
                                                                        </Text>
                                                                    </>
                                                                ) : (
                                                                    <Text size="xsmall" weight="strong" align="right">
                                                                        {formatCurrency(acc.amount || 0)}
                                                                    </Text>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </div>
                                                {hasMore && (
                                                    <Box display="flex" justifyContent="flexEnd">
                                                        <Actions>
                                                            <Button
                                                                variant="soft"
                                                                tone="neutral"
                                                                size="small" onClick={() => {
                                                                    setExpandedAccounts(prev => {
                                                                        const next = new Set(prev);
                                                                        if (isExpanded) next.delete(accKey); else next.add(accKey);
                                                                        return next;
                                                                    });
                                                                }}>
                                                                {isExpanded ? 'View less' : `View more (${allAccounts.length - 5} more)`}
                                                            </Button>
                                                        </Actions>
                                                    </Box>
                                                )}
                                            </Stack>
                                        );
                                    })()
                                ) : (
                                    renderAccounts(tx)
                                )}
                            </Stack>
                        </Column>
                        <Column width="content">
                            {(st === 'EXECUTED' || st === 'COMPLETED') && (tx.payload?.gl_transaction_header_id || wsCompletedInfo[tx.uuid]?.gl_transaction_header_id) && (
                                <Button size="small" variant="soft" tone="formAccent" icon={<IconPrint />} onClick={() => handlePrintCompletedTx(tx)} >Print</Button>
                            )}
                            {!tx.payload?.gl_transaction_header_id && !wsCompletedInfo[tx.uuid]?.gl_transaction_header_id && st !== 'REJECTED' && (
                                <Text size="xsmall" tone="secondary" align="right">Processing...</Text>
                            )}
                        </Column>
                    </Columns>
                </Stack>
            </Card>
        );
    };

    const renderList = (items: TransactionItem[], state: "pending" | "approved" | "rejected") => {
        if (loading) return <Text tone="secondary">Loading...</Text>;
        if (items.length === 0) return <Text tone="secondary">No transactions.</Text>;
        return <Stack space="small">{items.map(tx => renderCard(tx, state))}</Stack>;
    };

    const renderRecentList = (items: TransactionItem[]) => {
        return (
            <Box>
                {recentLoading && items.length === 0 ? (
                    <Text tone="secondary">Loading...</Text>
                ) : items.length === 0 ? (
                    <Text tone="secondary">No recent transactions for today.</Text>
                ) : (
                    <Stack space="small">
                        {items.map(tx => renderRecentCard(tx))}
                        {recentItems.length < recentTotal && (
                            <Box display="flex" justifyContent="flexEnd">
                                <Actions>
                                    <Button
                                        variant="soft"
                                        tone="brandAccent"
                                        loading={recentLoading} onClick={() => loadRecent(true)}>
                                        Load More ({recentTotal - recentItems.length} remaining)
                                    </Button>
                                </Actions>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        );
    };

    return (
        <Drawer title="Transactions In Approval Flow" width="large" open={open} onClose={onClose}>
            <TabsProvider>
                <Stack space="medium">
                    <Box display="flex" justifyContent="flexEnd">
                        <select
                            value={filterType}
                            onChange={(e: any) => setFilterType(e.currentTarget.value)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 6,
                                border: '1px solid #ccc',
                                fontSize: 14,
                                background: '#fff',
                                minWidth: 140,
                            }}
                        >
                            <option value="all">All Types</option>
                            <option value="receipt">Receipt</option>
                            <option value="payment">Payment</option>
                            <option value="withdrawal">Withdrawal</option>
                            <option value="journal">Journal</option>
                        </select>
                    </Box>
                    <Tabs label="Approval tabs" divider="full" size="small">
                        <Tab>Approved ({filterApproved.length})</Tab>
                        <Tab>Pending ({filterPending.length})</Tab>
                        <Tab>Rejected ({filterRejected.length})</Tab>
                        <Tab>Today Transactions ({recentTotal})</Tab>
                    </Tabs>
                    <TabPanels>
                        <TabPanel>{renderList(filterApproved, "approved")}</TabPanel>
                        <TabPanel>{renderList(filterPending, "pending")}</TabPanel>
                        <TabPanel>{renderList(filterRejected, "rejected")}</TabPanel>
                        <TabPanel>{renderRecentList(filterRecent)}</TabPanel>
                    </TabPanels>
                </Stack>
            </TabsProvider>

            {/* Credential Approval Dialog */}
            {credentialDialogOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999,
                }} onClick={() => {
                    if (!credentialLoading) {
                        setCredentialUsername('');
                        setCredentialPassword('');
                        setCredentialDialogOpen(false);
                    }
                }}>
                    <div style={{
                        width: 400, maxWidth: '90vw',
                        background: '#fff', borderRadius: 8,
                        padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    }} onClick={e => e.stopPropagation()}>
                        <form onSubmit={(e) => { e.preventDefault(); handleCredentialSubmit(); }} autoComplete="off">
                            <Stack space="medium">
                                <Text weight="strong" size="large">{actionType === 'APPROVE' ? 'Approve Transaction' : 'Reject Transaction'}</Text>
                                <Text size="small" tone="secondary">{actionType === 'APPROVE' ? 'Enter your credentials to approve this transaction' : 'Enter your credentials to reject this transaction'}</Text>
                                <TextField
                                    id="drawer-approval-username"
                                    label="Username"
                                    value={credentialUsername}
                                    onChange={(e: any) => setCredentialUsername(e.currentTarget.value)}
                                    autoComplete="off"
                                />
                                <TextField
                                    id="drawer-approval-password"
                                    label="Password"
                                    type="password"
                                    value={credentialPassword}
                                    onChange={(e: any) => setCredentialPassword(e.currentTarget.value)}
                                    autoComplete="new-password"
                                />
                                <Inline space="small" align="right">
                                    <Button variant="ghost" type="button" onClick={() => {
                                        setCredentialUsername('');
                                        setCredentialPassword('');
                                        setCredentialDialogOpen(false);
                                    }} loading={credentialLoading}>Cancel</Button>
                                    <Button tone="brandAccent" type="submit"
                                        loading={credentialLoading}
                                    >
                                        {credentialLoading ? 'Verifying...' : 'Submit'}
                                    </Button>
                                </Inline>
                            </Stack>
                        </form>
                    </div>
                </div>
            )}
            {printTxId !== null && (
                <TransactionPrintDrawer
                    transactionId={printTxId}
                    open={printTxId !== null}
                    onClose={() => setPrintTxId(null)}
                    onlyCredits={false}
                    onlyDebits={false}
                    doPrint={false}
                />
            )}
        </Drawer>
    );
};

export default TransactionApprovalDrawer;
